import { FloatingFormItem, PlanAccountsSelect } from '@/components/_common';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import TEFConnectionOverlay from '@/components/TEF/TEFConnectionOverlay';
import TEFProcessFinancial, {
  TEFProcessFinancialRef,
} from '@/components/TEF/TEFProcessFinancial';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { AccountReceivableSituation } from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { usePaymentTransactionCancel } from '@/core/domain/payment-gateway/transactions/transactions.hooks';
import {
  PaymentFinancialPendingStatus,
  TransactionEntityType,
  TransactionPaymentType,
} from '@/core/domain/payment-gateway/transactions/transactions.types';
import { useTefActivation, useTefCancel } from '@/core/domain/tef/tef.hooks';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Button, Form, Modal } from 'antd';
import moment from 'moment';
import { useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

export default function AccountReceivableTransactionCancel() {
  const { accountReceivable, currentInstallment } =
    useAccountReceivableContext();

  const paymentStatusRef = useRef<TEFProcessFinancialRef>(null);

  const entityId = useMemo(
    () => accountReceivable?.directSaleId || accountReceivable?.workOrderId,
    [accountReceivable?.directSaleId, accountReceivable?.workOrderId],
  );
  const entityType = useMemo(
    () =>
      accountReceivable?.directSaleId
        ? TransactionEntityType.Sale
        : TransactionEntityType.WorkOrder,
    [accountReceivable?.directSaleId, accountReceivable?.workOrderId],
  );

  const installment =
    accountReceivable?.installmentReceivables[currentInstallment];

  const isSituationSettled =
    installment?.situation === AccountReceivableSituation.settled;

  const [opened, action] = useDisclosure();

  const history = useHistory();

  const { data: company } = useCompany(
    opened ? accountReceivable?.companyId : undefined,
  );

  const hasCancelPermission = hasPermissions([Authorities.CANCEL_PAYMENTS_TEF]);

  const requiredField = useDefaultEmptyFieldError();

  const [form] = Form.useForm<{ planAccountId: number }>();

  const transactionCancel = usePaymentTransactionCancel();
  const tefCancel = useTefCancel();
  const tefActivation = useTefActivation(company?.cnpj, opened);

  const [infoOpen, infoAction] = useDisclosure(true);

  const isLoading = transactionCancel.isLoading || tefCancel.isLoading;

  const isWithinDeadline = useMemo(() => {
    const createdAt = accountReceivable?.transaction?.createdAt;

    const dateToCompare = moment(createdAt);
    const now = moment();

    return !!createdAt && now.diff(dateToCompare, 'hours') < 24;
  }, [accountReceivable?.transaction?.createdAt]);

  const cancelPayment = useCallback(
    async (planAccountId?: number) => {
      try {
        if (!accountReceivable?.transaction) return;

        const transaction = accountReceivable?.transaction;

        const date = moment(transaction.createdAt).format('DD/MM/YY');

        const result = await tefCancel.mutateAsync({
          data: date,
          nsu: transaction.terminalNsu,
          valor: transaction.totalAmount,
        });

        if (result.resultado?.tef.retorno === '1') {
          await transactionCancel.mutateAsync({
            code: String(accountReceivable.code),
            planAccountId: planAccountId,
            transactionId: transaction.id,
          });

          await paymentStatusRef.current?.statusRefetch();
        }

        handleClose();
      } catch (error) {
        console.log(error);
      }
    },
    [accountReceivable?.transaction],
  );

  const confirmToCancelPayment = useCallback(() => {
    Modal.confirm({
      width: 500,
      title: 'Cancelamento de pagamento via TEF',
      content:
        'Ao cancelar o pagamento, este lançamento de contas a receber será cancelado após a confirmação do cancelamento da compra via maquininha.',
      onOk: () => cancelPayment(),
      okText: 'Confirmar',
      cancelText: 'Fechar',
    });
  }, [cancelPayment]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    cancelPayment(values.planAccountId);
  };

  const handleClose = useCallback(() => {
    if (isLoading) return;

    action.close();
    form.resetFields();
  }, [isLoading]);

  if (
    !accountReceivable?.transaction?.id ||
    !isWithinDeadline ||
    accountReceivable.transaction.paymentType === TransactionPaymentType.Pix
  )
    return null;

  return (
    <>
      <TEFProcessFinancial
        ref={paymentStatusRef}
        enabled={transactionCancel.isSuccess}
        entityId={entityId}
        isAccountReceivable
        entityType={entityType}
        refresh={(status) => {
          if (status === PaymentFinancialPendingStatus.CANCELED) {
            transactionCancel.reset();
            history.push('/app/financial/billToReceiver/releaseBillToReceiver');
          }
        }}
      />

      <Button
        danger
        type="primary"
        onClick={isSituationSettled ? action.open : confirmToCancelPayment}
        loading={isLoading}
        disabled={!hasCancelPermission}
      >
        Cancelar pagamento (TEF)
      </Button>

      <Modal
        open={opened}
        width={600}
        title="Cancelamento de pagamento via TEF"
        okText="Solicitar cancelamento"
        confirmLoading={isLoading}
        closable={!isLoading}
        maskClosable={!isLoading}
        onCancel={handleClose}
        onOk={handleSubmit}
        closeIcon={isLoading ? null : undefined}
        okButtonProps={{ disabled: isLoading }}
        cancelButtonProps={{ disabled: isLoading }}
        style={{ position: 'relative' }}
      >
        {transactionCancel.isLoading && (
          <ModalLoadingOverlay
            style={{ borderRadius: 10 }}
            messages={['Processando cancelamento...']}
            posAbsolute
          />
        )}

        {tefActivation.isLoading && (
          <ModalLoadingOverlay
            style={{ borderRadius: 10 }}
            messages={['Analisando integração...']}
            posAbsolute
          />
        )}

        <TEFConnectionOverlay
          onClose={handleClose}
          visible={!tefActivation.isSuccess && !tefActivation.isLoading}
        />

        {opened && infoOpen && (
          <ModalLoadingOverlay
            disableLoading
            style={{ borderRadius: 10, textAlign: 'center' }}
            messages={[
              <div style={{ textAlign: 'center' }}>
                <div>
                  Antes de solicitar o cancelamento verifique se os programas
                  Elgin TEFHUB e E1_Connect estão abertos corretamente.
                </div>

                <Button onClick={infoAction.close}>Estão abertos!</Button>
              </div>,
            ]}
            posAbsolute
          />
        )}

        <p>
          <b>Atenção:</b> Ao solicitar o cancelamento do pagamento, o título em
          Contas a Receber e os lançamentos bancários vinculados serão
          automaticamente estornados e cancelados pelo sistema. Esse processo
          será executado internamente, e você será notificado assim que for
          concluído.
        </p>

        <p>⚠️ Para prosseguir, informe o plano de contas para o estorno.</p>

        <Form form={form}>
          <FloatingFormItem
            name="planAccountId"
            label="Plano de contas"
            rules={[requiredField]}
          >
            <PlanAccountsSelect
              disabled={isLoading}
              queryFilter={{
                active: true,
                hierarchy: PlanAccountHierarchy.Analitical,
                destination: PlanAccountDestination.Expense,
              }}
            />
          </FloatingFormItem>
        </Form>
      </Modal>
    </>
  );
}
